// ** react imports
import React, { useState, useMemo, useEffect, useCallback } from 'react';

// ** views imports
import TaxFileTable from '../../../views/taxFile/TaxFileList/TaxFileTable';
import TaxFileDatePickerWithRange from '../../../views/taxFile/TaxFileList/TaxFileDatePicker';
import TaxFilePagination from '../../../views/taxFile/TaxFileList/TaxFilePagination';
import TaxFileDropdownFilterButton from '../../../views/taxFile/TaxFileList/TaxFileDropdownFilterButton';

// ** partials imports
import DeleteButton from '../../../partials/actions/DeleteButton';

// ** custom hooks imports
import { useGetAllTaxFiles } from '../../../hooks/apis/useTaxFile';

const TaxFileList = props => {
  // props
  const {} = props;

  // states
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    fileStatus: '',
    submissionType: 'ALL',
    search: '',
    startDate: null,
    endDate: null,
  });

  // Status labels mapping
  const taxFileStatusLabels = {
    created: 'Created',
    in_progress: 'In Progress',
    completed: 'Completed',
    submitted: 'Submitted',
    processed: 'Processed',
    archived: 'Archived',
  };

  // tax files query
  const {
    data: taxFiles,
    isGetAllTaxFilesLoading,
    isGetAllTaxFilesFetching,
    getAllTaxFiles,
  } = useGetAllTaxFiles();

  useEffect(() => {
    getAllTaxFiles(query);
  }, [query, getAllTaxFiles]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== query.search) {
        setQuery(prev => ({
          ...prev,
          search: searchTerm,
          page: 1,
        }));
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query.search, searchTerm]);

  const tableData = useMemo(() => {
    return {
      data: Array.isArray(taxFiles?.data) ? taxFiles.data : [],
      total: typeof taxFiles?.total === 'number' ? taxFiles.total : 0,
    };
  }, [taxFiles]);

  // handlers
  const handleSelectedItems = selectedItems => {
    setSelectedItems([...selectedItems]);
  };

  // Filter handlers
  const handlePageChange = newPage => {
    setQuery(prev => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleLimitChange = newLimit => {
    setQuery(prev => ({
      ...prev,
      limit: newLimit,
      page: 1,
    }));
  };

  const handleSortChange = (newSortBy, newSortOrder) => {
    setQuery(prev => ({
      ...prev,
      sortBy: newSortBy,
      sortOrder: newSortOrder,
    }));
  };

  const handleStatusFiltersChange = fileStatus => {
    setQuery(prev => ({
      ...prev,
      fileStatus,
      page: 1,
    }));
  };

  const handleDateFilter = (dateFrom, dateTo) => {
    setQuery(prev => ({
      ...prev,
      dateFrom: dateFrom ? dateFrom.toISOString() : null,
      dateTo: dateTo ? dateTo.toISOString() : null,
      page: 1,
    }));
  };

  const handleSearchFilter = useCallback(value => {
    setSearchTerm(value);
  }, []);

  const handleSubmissionTypeFilter = submissionType => {
    setQuery(prev => ({
      ...prev,
      submissionType,
      page: 1,
    }));
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setResetKey(prev => prev + 1);
    setQuery({
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      fileStatus: '',
      submissionType: 'ALL',
      search: '',
      dateFrom: null,
      dateTo: null,
    });
  };

  return (
    <div className="flex">
      <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-[96rem] mx-auto">
          <div className="sm:flex sm:justify-between sm:items-center mb-6">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                Tax Files
              </h1>
            </div>

            <div className="grid grid-flow-col justify-start sm:justify-end gap-2">
              <div className="relative h-full">
                <div className="absolute inset-y-0 left-0 top-0 bottom-1 flex items-center pl-3 pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="fill-current text-gray-400 dark:text-gray-500 ml-1 mr-2 shrink-0"
                    width="19"
                    height="19"
                    viewBox="0 0 20 20"
                  >
                    <path d="M 9 2 C 5.1458514 2 2 5.1458514 2 9 C 2 12.854149 5.1458514 16 9 16 C 10.747998 16 12.345009 15.348024 13.574219 14.28125 L 14 14.707031 L 14 16 L 19.585938 21.585938 C 20.137937 22.137937 21.033938 22.137938 21.585938 21.585938 C 22.137938 21.033938 22.137938 20.137938 21.585938 19.585938 L 16 14 L 14.707031 14 L 14.28125 13.574219 C 15.348024 12.345009 16 10.747998 16 9 C 16 5.1458514 12.854149 2 9 2 z M 9 4 C 11.773268 4 14 6.2267316 14 9 C 14 11.773268 11.773268 14 9 14 C 6.2267316 14 4 11.773268 4 9 C 4 6.2267316 6.2267316 4 9 4 z"></path>
                  </svg>
                </div>
                <input
                  type="text"
                  className="btn h-full w-[300px] pl-12 pr-4 bg-white border-gray-200 hover:border-gray-300 dark:border-gray-700/60 dark:hover:border-gray-600 dark:bg-gray-800 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 cursor-default"
                  placeholder="Search by reference, client name..."
                  value={searchTerm}
                  onChange={e => handleSearchFilter(e.target.value)}
                />
              </div>

              <DeleteButton selectedItems={selectedItems} />

              <TaxFileDatePickerWithRange
                onDateChange={handleDateFilter}
                initialDateRange={
                  query.dateFrom && query.dateTo
                    ? {
                        from: new Date(query.dateFrom),
                        to: new Date(query.dateTo),
                      }
                    : query.dateFrom
                      ? { from: new Date(query.dateFrom), to: null }
                      : null
                }
              />

              <TaxFileDropdownFilterButton
                key={resetKey}
                align="right"
                onSubmissionTypeChange={handleSubmissionTypeFilter}
                onStatusFiltersChange={handleStatusFiltersChange}
                onSearchChange={handleSearchFilter}
                onReset={handleResetFilters}
                fileStatus={query.fileStatus}
                submissionType={query.submissionType}
                searchTerm={query.search}
              />
            </div>
          </div>

          {/* Active Filters Display */}
          {(query.submissionType !== 'ALL' ||
            query.search ||
            query.dateFrom ||
            query.fileStatus.length > 0) && (
            <div className="mb-4 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-700 transition-all duration-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start sm:items-center gap-2 text-sm text-violet-700 dark:text-violet-300">
                  <svg
                    className="w-4 h-4 mt-0.5 sm:mt-0 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
                    />
                  </svg>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="font-medium whitespace-nowrap">
                      Active Filters:
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {query.submissionType !== 'ALL' && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Type:{' '}
                          {query.submissionType === 'VIRTUAL_INTAKE'
                            ? 'Virtual Intake'
                            : 'In-Office'}
                        </span>
                      )}
                      {query.search && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Search: &quot;
                          {query.search.length > 20
                            ? query.search.substring(0, 20) + '...'
                            : query.search}
                          &quot;
                        </span>
                      )}
                      {query.dateFrom && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Date Range:{' '}
                          {new Date(query.dateFrom).toLocaleDateString()} -{' '}
                          {query.dateTo
                            ? new Date(query.dateTo).toLocaleDateString()
                            : 'Present'}
                        </span>
                      )}
                      {query.fileStatus.split(',').map(status => {
                        const trimmedStatus = status.trim();
                        return trimmedStatus ? (
                          <span
                            key={trimmedStatus}
                            className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium"
                          >
                            Status:{' '}
                            {taxFileStatusLabels[trimmedStatus] ||
                              trimmedStatus.replace('_', ' ')}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleResetFilters}
                  className="text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 text-sm font-medium whitespace-nowrap transition-colors self-start sm:self-center cursor-pointer"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <TaxFileTable
            data={tableData}
            selectedItems={handleSelectedItems}
            isGetAllTemplatesFetching={isGetAllTaxFilesFetching}
            isGetAllTemplatesLoading={
              isGetAllTaxFilesLoading || isGetAllTaxFilesFetching
            }
            onSortChange={handleSortChange}
            currentSortBy={query.sortBy}
            currentSortOrder={query.sortOrder}
          />

          {/* Pagination */}
          <div className="mt-6">
            <TaxFilePagination
              currentPage={query.page}
              totalPages={
                tableData?.total ? Math.ceil(tableData.total / query.limit) : 1
              }
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              currentLimit={query.limit}
              totalItems={tableData?.total || 0}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

TaxFileList.propTypes = {};

export default TaxFileList;
